import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { useAuth } from "../auth/useAuth.js";
import { Pagina } from "../../components/Pagina.js";
import {
  Botao,
  Campo,
  Chave,
  Entrada,
  Falha,
  Girando,
  Selecao,
  Vazio,
} from "../../components/ui.js";
import { montarLinkDaGaleria } from "../galeria/link.js";
import { EscolhaDeMusicaNaGaleria } from "../galeria/EscolhaDeMusica.js";
import { PREFERENCIAS_PADRAO, type PreferenciasGaleria } from "../galeria/preferencias.js";

/**
 * Como montar a projeção na TV.
 *
 * A pessoa que lê esta tela está de pé, com um controle remoto na outra mão,
 * geralmente minutos antes de o evento começar. Por isso a tela é curta e o
 * código é a coisa maior nela — não um detalhe dentro de um parágrafo.
 *
 * Para o admin é o quiosque do suporte: o mesmo código e os mesmos passos, com
 * um a mais na TV — escolher de qual cliente projetar.
 */
export function KioskPage() {
  const { ehAdmin } = useAuth();
  const quiosque = trpc.kiosk.meu.useQuery();
  const utils = trpc.useUtils();
  const [copiado, setCopiado] = useState(false);

  const regerar = trpc.kiosk.regerarCodigo.useMutation({
    onSuccess: () => void utils.kiosk.meu.invalidate(),
  });
  const criar = trpc.kiosk.criar.useMutation({
    onSuccess: () => void utils.kiosk.meu.invalidate(),
  });

  const endereco = `${window.location.origin}/galeria`;
  const codigo = quiosque.data?.kioskCode ?? "";

  async function copiar() {
    await navigator.clipboard.writeText(codigo);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <Pagina
      titulo="Kiosk"
      descricao={
        ehAdmin
          ? "O que digitar na TV para projetar a galeria de qualquer cliente."
          : "O que digitar na TV para abrir a galeria do seu evento."
      }
    >
      {quiosque.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : quiosque.isError ? (
        <Falha mensagem={quiosque.error.message} />
      ) : !quiosque.data && ehAdmin ? (
        <>
          <Vazio
            titulo="Você ainda não tem quiosque"
            descricao="O quiosque do suporte abre a galeria de qualquer cliente: na TV, você escolhe o cliente e a vivência."
            acao={
              <Botao tom="brasa" onClick={() => criar.mutate()} carregando={criar.isPending}>
                Criar o quiosque
              </Botao>
            }
          />
          {criar.isError && <Falha mensagem={criar.error.message} />}
        </>
      ) : !quiosque.data ? (
        <Vazio
          titulo="Esta conta não tem quiosque"
          descricao="Fale com o suporte para criar o quiosque desta conta."
        />
      ) : (
        <div className="flex flex-col gap-5">
          <ol className="flex flex-col gap-4">
            <Passo numero={1} titulo="Abra o endereço no navegador da TV">
              <p className="break-all font-mono text-[0.9375rem] text-texto">{endereco}</p>
            </Passo>

            <Passo numero={2} titulo="Digite o código">
              <div className="flex flex-wrap items-center gap-3">
                {/* Tamanho e espaçamento pensados para leitura a alguns metros:
                    quem digita costuma estar longe da tela onde o código está. */}
                <span className="num font-mono text-[2rem] leading-none tracking-[0.2em] text-brasa">
                  {codigo}
                </span>
                <Botao tom="quieto" onClick={() => void copiar()}>
                  {copiado ? "Copiado" : "Copiar"}
                </Botao>
              </div>
            </Passo>

            <Passo
              numero={3}
              titulo={
                ehAdmin
                  ? "Escolha o cliente, a vivência e toque em Iniciar"
                  : "Escolha a vivência e toque em Iniciar"
              }
            >
              <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
                A projeção abre em tela cheia e as fotos aprovadas entram sozinhas.
              </p>
            </Passo>
          </ol>

          <LinkParaATv codigo={codigo} ehAdmin={ehAdmin} />

          <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
            <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
              O código só abre a galeria: não atende, não apaga e não envia nada. Se ele aparecer
              numa foto do salão ou for parar em mãos erradas, troque — os links copiados antes
              param de valer, e as TVs já abertas seguem funcionando até serem reiniciadas.
            </p>
            <div className="mt-3">
              <Botao
                tom="descartar"
                onClick={() => regerar.mutate()}
                carregando={regerar.isPending}
              >
                Gerar outro código
              </Botao>
            </div>
            {regerar.isError && (
              <div className="mt-3">
                <Falha mensagem={regerar.error.message} />
              </div>
            )}
          </div>
        </div>
      )}
    </Pagina>
  );
}

/**
 * O link que põe uma TV no ar sem ninguém configurar nada.
 *
 * As mesmas escolhas da tela da galeria, feitas aqui, por quem sabe o que o
 * evento quer — e levadas no link para quem só vai ligar a TV. Cada link leva a
 * sua configuração: duas TVs, duas vivências, dois links.
 */
function LinkParaATv({ codigo, ehAdmin }: { codigo: string; ehAdmin: boolean }) {
  const { usuario } = useAuth();
  const vivencias = trpc.vivencias.list.useQuery();
  const playlists = trpc.playlists.list.useQuery();
  const musicas = trpc.musicas.list.useQuery();
  // Só o admin escolhe de quem projetar: a TV do suporte serve a qualquer cliente.
  const usuarios = trpc.users.list.useQuery(undefined, { enabled: ehAdmin });

  const [donoId, setDonoId] = useState<number | null>(null);
  const [vivenciaId, setVivenciaId] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<PreferenciasGaleria>(PREFERENCIAS_PADRAO);
  const [copiado, setCopiado] = useState(false);
  const mudar = (parcial: Partial<PreferenciasGaleria>) =>
    setPrefs((atual) => ({ ...atual, ...parcial }));

  const clientes = (usuarios.data ?? []).filter((u) => u.role === "cliente");

  // As que a TV vai oferecer: ativas, do dono projetado e as compartilhadas. Para
  // o cliente, o servidor já recorta; para o admin, o recorte é o do cliente
  // escolhido — "Você (suporte)" é o próprio admin.
  const donoProjetado = ehAdmin ? (donoId ?? usuario?.id ?? null) : null;
  const opcoes = (vivencias.data ?? []).filter(
    (v) =>
      v.status === "active" &&
      (donoProjetado === null || v.ownerUserId === null || v.ownerUserId === donoProjetado),
  );

  // As playlists que aquela TV vai enxergar: as do sistema e as do dono
  // projetado — a mesma régua das vivências.
  const playlistsDaTv = (playlists.data ?? [])
    .filter(
      (p) => donoProjetado === null || p.ownerUserId === null || p.ownerUserId === donoProjetado,
    )
    .map((p) => ({ id: p.id, nome: p.nome, quantas: p.musicaIds.length }));
  const musicasDaTv = (musicas.data ?? []).map((m) => ({ id: m.id, titulo: m.titulo }));

  const link = montarLinkDaGaleria(window.location.origin, {
    codigo,
    vivenciaId,
    donoId: ehAdmin ? donoId : null,
    preferencias: prefs,
  });

  async function copiar() {
    await navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <section className="flex flex-col gap-4 rounded-peca border border-borda bg-superficie px-4 py-4">
      <div>
        <h2 className="text-[0.9375rem] font-medium text-texto">Link para a TV</h2>
        <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
          Escolha como a galeria aparece e mande o link para quem vai ligar a TV: abrindo, ela entra
          e começa a passar sozinha. O primeiro toque na tela põe em tela cheia.
        </p>
      </div>

      {ehAdmin && (
        <Campo rotulo="Projetar">
          <Selecao
            value={donoId ?? ""}
            onChange={(e) => {
              setDonoId(e.target.value === "" ? null : Number(e.target.value));
              // A vivência escolhida era do dono anterior — e a playlist, se
              // era de um cliente, também.
              setVivenciaId(null);
              const escolhida = prefs.musica;
              if (
                escolhida.tipo === "playlist" &&
                playlists.data?.find((p) => p.id === escolhida.id)?.ownerUserId !== null
              ) {
                mudar({ musica: { tipo: "nenhuma" } });
              }
            }}
          >
            <option value="">Você (suporte)</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Selecao>
        </Campo>
      )}

      <Campo rotulo="Mostrar fotos de">
        <Selecao
          value={vivenciaId ?? ""}
          onChange={(e) => setVivenciaId(e.target.value === "" ? null : e.target.value)}
        >
          <option value="">Todas as vivências</option>
          {opcoes.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Selecao>
      </Campo>

      <div className="flex flex-col gap-3 border-t border-borda pt-4">
        <Linha rotulo="Nome do espectador" dica="Legenda sobre a foto">
          <Chave
            rotulo="Nome do espectador"
            ligada={prefs.mostrarNome}
            aoAlternar={(ligada) => mudar({ mostrarNome: ligada })}
          />
        </Linha>
        <Linha rotulo="Divulgações" dica="Peças do acervo entre as fotos">
          <Chave
            rotulo="Divulgações"
            ligada={prefs.divulgacoes}
            aoAlternar={(ligada) => mudar({ divulgacoes: ligada })}
          />
        </Linha>
        {prefs.divulgacoes && (
          <Linha rotulo="A cada" dica="Quantas fotos entre uma divulgação e outra">
            <Entrada
              type="number"
              min={1}
              max={50}
              inputMode="numeric"
              placeholder="5"
              value={prefs.intervalo ?? ""}
              onChange={(e) => {
                const n = Number(e.target.value);
                mudar({ intervalo: Number.isInteger(n) && n >= 1 && n <= 50 ? n : null });
              }}
              className="w-20 text-center"
            />
          </Linha>
        )}
        <Linha rotulo="Áudio" dica="Som nas divulgações em vídeo">
          <Chave
            rotulo="Áudio"
            ligada={prefs.audio}
            aoAlternar={(ligada) => mudar({ audio: ligada })}
          />
        </Linha>
      </div>

      <div className="border-t border-borda pt-4">
        <EscolhaDeMusicaNaGaleria
          playlists={playlistsDaTv}
          musicas={musicasDaTv}
          valor={prefs}
          aoMudar={mudar}
        />
      </div>

      <div className="flex flex-col gap-3 border-t border-borda pt-4">
        <p className="break-all font-mono text-[0.75rem] leading-relaxed text-texto-fraco">
          {link}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Botao tom="brasa" onClick={() => void copiar()}>
            {copiado ? "Copiado" : "Copiar link"}
          </Botao>
          <p className="text-[0.75rem] leading-snug text-texto-fraco">
            O link leva o código: mande só para quem vai ligar a TV.
          </p>
        </div>
      </div>
    </section>
  );
}

function Linha({
  rotulo,
  dica,
  children,
}: {
  rotulo: string;
  dica: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[0.875rem] text-texto">{rotulo}</p>
        <p className="text-[0.75rem] text-texto-fraco">{dica}</p>
      </div>
      {children}
    </div>
  );
}

function Passo({
  numero,
  titulo,
  children,
}: {
  numero: number;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="num mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-superficie-alta text-[0.8125rem] text-texto-suave">
        {numero}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[0.9375rem] font-medium text-texto">{titulo}</p>
        <div className="mt-1.5">{children}</div>
      </div>
    </li>
  );
}
